import store from '../../redux/store';
import { updateParticle } from '../../redux/Slice/particleSlice';
import {
  pauseAnimation,
  resetParticleSystem,
  startAnimation,
  stopAnimation,
} from './animationControls';
import { AnimationManager } from './handleClouds';
import {
  clearLifeTimeout,
  setLifeTimeout,
  suspendParticleLifecycleForSelection,
  resumeParticleLifecycleAfterSelection,
} from './lifeUtils';
import { applyParticleRuntimeHidden } from '../ThreeJsUtils/assetVisibilityUtils';

export const PARTICLE_ANIMATED_OBJ_TYPES = new Set([
  'foliage',
  'clouds',
  'rain',
  'snow',
  'fire',
  'stars',
  'water',
  'terrain',
]);

export function isParticleAnimationRoot(obj) {
  if (!obj) return false;
  return (
    obj.deleteType === 'particle' ||
    PARTICLE_ANIMATED_OBJ_TYPES.has(obj.objType)
  );
}

/** True when object is a particle system root or any of its animated descendants. */
export function isUnderParticleAnimationSystem(obj, sceneRoot) {
  let node = obj;
  while (node && node !== sceneRoot) {
    if (isParticleAnimationRoot(node)) return true;
    node = node.parent;
  }
  return false;
}

export function restartParticlePlayback(obj, particleConfig = {}) {
  if (!obj) return;

  const particleId = obj.name || obj.url || obj.uuid;
  // Reset emitter state (positions/life arrays) before restarting the loop.
  try {
    resetParticleSystem(obj, particleId);
  } catch (_) {
    /* ignore */
  }

  const ud = obj.userData || {};
  ud.lifeExpired = false;
  ud.runtimeHidden = false;
  ud.isExpired = false;
  ud.isStopped = particleConfig.isStopped === true;
  ud.isPlaying = particleConfig.isPlaying !== false;
  obj.visible = true;
  applyParticleRuntimeHidden(obj, false);

  const lifeVal = particleConfig.life ?? ud.life;
  const lifeSeconds =
    typeof lifeVal === 'number' ? lifeVal : parseFloat(lifeVal) || 0;

  // Stars drive expiry via remainingLife in the animate loop (not setTimeout).
  if (lifeSeconds > 0) {
    ud.remainingLife = lifeSeconds;
    ud.life = lifeSeconds;
  } else if (
    typeof particleConfig.life !== 'undefined' ||
    typeof ud.remainingLife === 'number'
  ) {
    ud.remainingLife =
      typeof particleConfig.life === 'number' ? particleConfig.life : null;
  }

  clearLifeTimeout(obj);
  if (lifeSeconds > 0) {
    setLifeTimeout(obj, lifeSeconds);
  }

  if (ud.isStopped) {
    return;
  }

  if (obj.objType === 'clouds') {
    ud.isPaused = false;
    AnimationManager.register(obj);
    return;
  }

  const animateFunction = ud.animateFunction;
  if (typeof animateFunction === 'function') {
    ud.isPaused = false;
    startAnimation(obj, animateFunction, particleId);
  }
}

export function findParticleConfigForObject(obj, particles) {
  return particles.find(
    (p) =>
      p.id === obj.name ||
      p.id === obj.url ||
      p.name === obj.name ||
      p.temp_file_name === obj.name
  );
}

export function resolveParticleAnimationRoot(obj) {
  if (!obj) return null;
  if (isParticleAnimationRoot(obj)) return obj;

  let current = obj.parent;
  while (current) {
    if (isParticleAnimationRoot(current)) return current;
    current = current.parent;
  }
  return null;
}

export function isParticleLifeEnded(obj) {
  // Lifecycle only applies in Preview — design selection must always work.
  try {
    if (store.getState()?.window?.window_type !== 'preview') return false;
  } catch (_) {
    return false;
  }

  const root = resolveParticleAnimationRoot(obj);
  if (!root) return false;

  const ud = root.userData || {};
  return !!(
    ud.lifeExpired ||
    ud.runtimeHidden ||
    ud.isExpired ||
    (typeof ud.remainingLife === 'number' && ud.remainingLife <= 0)
  );
}

/** Restart life-ended particles when selected from the hierarchy / viewport. */
export function reviveParticleOnSelect(object) {
  if (!object || !isParticleAnimationRoot(object)) return;

  const particles = store.getState().particles?.particles || [];
  const config = findParticleConfigForObject(object, particles) || {};
  if (config.visible === false) return;

  const needsRevive =
    object.userData?.lifeExpired ||
    object.userData?.runtimeHidden ||
    object.userData?.isExpired ||
    object.visible === false ||
    (typeof object.userData?.remainingLife === 'number' &&
      object.userData.remainingLife <= 0);

  if (needsRevive) {
    const particleId = object.name || object.url || config.id;
    restartParticlePlayback(object, {
      ...config,
      isPlaying: true,
      isStopped: false,
    });

    if (particleId) {
      store.dispatch(
        updateParticle({
          id: particleId,
          updates: { isPlaying: true, isStopped: false },
        })
      );
    }
  }

  // Design mode: keep the selected particle alive while editing it.
  try {
    if (store.getState()?.window?.window_type === 'design') {
      suspendParticleLifecycleForSelection(object);
    }
  } catch (_) {
    /* ignore */
  }
}

/** Resume lifecycle timers when a particle is deselected in design. */
export function releaseParticleLifecycleOnDeselect(object) {
  if (!object || !isParticleAnimationRoot(object)) return;
  resumeParticleLifecycleAfterSelection(object);
}

/**
 * Restart particle lifecycle + loops on preview enter/exit/replay.
 * Uses Redux play/stop as source of truth so life-expired scene flags
 * (userData.isStopped from setLifeTimeout) do not permanently skip revive.
 */
export function resumeSceneParticleAnimations(scene) {
  if (!scene) return;

  const particles = store.getState().particles?.particles || [];

  scene.traverse((obj) => {
    if (!isParticleAnimationRoot(obj)) return;

    const config = findParticleConfigForObject(obj, particles) || {};
    if (config.visible === false) return;

    // Intentional pause/stop from Redux only — ignore lifecycle-expired userData.
    const isStopped = config.isStopped === true;
    const isPaused = config.isPlaying === false && config.isStopped !== true;

    if (isStopped || isPaused) {
      return;
    }

    restartParticlePlayback(obj, {
      ...config,
      isPlaying: true,
      isStopped: false,
    });
  });
}

/**
 * Re-apply particle play/pause/stop flags from Redux after undo/redo restore.
 * This prevents particle loops from staying frozen after history actions.
 */
export function syncSceneParticlePlaybackFromRedux(scene) {
  if (!scene) return;

  const particles = store.getState().particles?.particles || [];

  scene.traverse((obj) => {
    if (!isParticleAnimationRoot(obj)) return;

    const config = findParticleConfigForObject(obj, particles) || {};
    if (config.visible === false) return;

    const particleId = config.id || obj.name || obj.url || obj.uuid;

    if (config.isStopped === true) {
      stopAnimation(obj, particleId);
      resetParticleSystem(obj, particleId);
      obj.userData.isPlaying = false;
      obj.userData.isStopped = true;
      obj.userData.isPaused = false;
      return;
    }

    if (config.isPlaying === false) {
      pauseAnimation(obj, particleId);
      obj.userData.isPlaying = false;
      obj.userData.isStopped = false;
      obj.userData.isPaused = true;
      return;
    }

    restartParticlePlayback(obj, {
      ...config,
      isPlaying: true,
      isStopped: false,
    });
  });
}
